import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Space, Modal, Form, Input, Select, Tag, message, Popconfirm,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as smsService from '../../services/smsService';

const { TextArea } = Input;

const SmsTemplate: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<smsService.SmsTemplate[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchName, setSearchName] = useState('');
  const [searchType, setSearchType] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<smsService.SmsTemplate | null>(null);
  const [form] = Form.useForm();
  const [channels, setChannels] = useState<smsService.SmsChannel[]>([]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await smsService.listTemplates({
        page, pageSize,
        name: searchName || undefined,
        templateType: searchType || undefined,
      });
      setTemplates(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('加载模板列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadChannels = async () => {
    try {
      const res = await smsService.getAllChannels();
      setChannels(res.data || []);
    } catch { }
  };

  useEffect(() => { loadTemplates(); loadChannels(); }, [page, pageSize]);

  const handleSearch = () => { setPage(1); loadTemplates(); };
  const handleReset = () => { setSearchName(''); setSearchType(''); setPage(1); loadTemplates(); };

  const handleAdd = () => {
    setEditingTemplate(null);
    form.resetFields();
    form.setFieldsValue({ status: 1, templateType: 'verify' });
    setModalVisible(true);
  };

  const handleEdit = (record: smsService.SmsTemplate) => {
    setEditingTemplate(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await smsService.deleteTemplate(id);
      message.success('删除成功');
      loadTemplates();
    } catch (err: any) {
      message.error(err.response?.data?.message || '删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingTemplate) {
        await smsService.updateTemplate(editingTemplate.id, values);
        message.success('更新成功');
      } else {
        await smsService.createTemplate(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadTemplates();
    } catch { }
  };

  const columns: ColumnsType<smsService.SmsTemplate> = [
    { title: '模板名称', dataIndex: 'name', key: 'name', width: 120 },
    {
      title: '模板编码',
      dataIndex: 'code',
      key: 'code',
      width: 160,
      render: (v) => smsService.templateCodeOptions.find(o => o.value === v)?.label || v,
    },
    { title: '服务商模板ID', dataIndex: 'templateId', key: 'templateId', width: 140 },
    {
      title: '模板类型', dataIndex: 'templateType', key: 'templateType', width: 90,
      render: (v) => {
        const opt = smsService.templateTypeOptions.find(o => o.value === v);
        const colors: Record<string, string> = { verify: 'blue', notify: 'green', marketing: 'orange' };
        return <Tag color={colors[v] || 'default'}>{opt?.label || v}</Tag>;
      },
    },
    { title: '所属渠道', dataIndex: ['channel', 'name'], key: 'channel', width: 100 },
    { title: '模板内容', dataIndex: 'content', key: 'content', ellipsis: true },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 70,
      render: (v) => <Tag color={v === 1 ? 'green' : 'red'}>{v === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '操作', key: 'action', width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除此模板？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="短信模板"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadTemplates}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增模板</Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16 }}>
          <Input placeholder="模板名称" value={searchName} onChange={(e) => setSearchName(e.target.value)} style={{ width: 150 }} allowClear />
          <Select placeholder="模板类型" value={searchType || undefined} onChange={setSearchType} style={{ width: 120 }} allowClear>
            {smsService.templateTypeOptions.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
          </Select>
          <Button type="primary" onClick={handleSearch}>搜索</Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
        <Table columns={columns} dataSource={templates} rowKey="id" loading={loading}
          pagination={{ current: page, pageSize, total, showSizeChanger: true, showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); } }} />
      </Card>

      <Modal title={editingTemplate ? '编辑模板' : '新增模板'} open={modalVisible} onCancel={() => setModalVisible(false)} onOk={handleSubmit} width={600}>
        <Form form={form} layout="vertical">
          <Form.Item name="channelId" label="所属渠道" rules={[{ required: true, message: '请选择渠道' }]}>
            <Select placeholder="请选择渠道">
              {channels.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="name" label="模板名称" rules={[{ required: true, message: '请输入模板名称' }]}>
            <Input placeholder="请输入模板名称" />
          </Form.Item>
          <Form.Item name="code" label="模板编码" rules={[{ required: true, message: '请选择模板编码' }]}>
            <Select placeholder="请选择模板编码" disabled={!!editingTemplate}>
              {smsService.templateCodeOptions.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="templateId" label="服务商模板ID" rules={[{ required: true, message: '请输入服务商模板ID' }]}>
            <Input placeholder="在服务商平台申请的模板ID" />
          </Form.Item>
          <Form.Item name="templateType" label="模板类型" rules={[{ required: true, message: '请选择模板类型' }]}>
            <Select>
              {smsService.templateTypeOptions.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="content" label="模板内容">
            <TextArea rows={3} placeholder="模板内容，如: 您的验证码是${code}，5分钟内有效" />
          </Form.Item>
          <Form.Item name="params" label="参数列表">
            <Input placeholder='JSON格式，如: ["code"]' />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select>
              <Select.Option value={1}>启用</Select.Option>
              <Select.Option value={0}>禁用</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SmsTemplate;
